import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #checkingGift = false;
  
  // Confetti Memory Tracker & Pending Queue
  #lastUnlockedCount = parseInt(sessionStorage.getItem('aura_unlocked_memory') || '0', 10);
  #pendingConfetti = null; // Stores milestone data if drawer is closed
  #drawerObserver = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(ThemeEvents.cartAdd, this.#checkFreeGift);

    document.addEventListener('click', this.#handleUpsellClick);

    // Watch the Drawer Dialog for opening events
    const dialog = this.closest('dialog');
    if (dialog) {
      this.#drawerObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
             // If the drawer just opened AND we have a pending blast
            if (dialog.hasAttribute('open')) {
              // 1. Check Pending Confetti
              if (this.#pendingConfetti) {
                setTimeout(() => {
                  this.#triggerConfetti(this.#pendingConfetti.isMaxed);
                  this.#pendingConfetti = null; 
                }, 400); 
              }
              // 2. Check Pending Toast (if drawer was closed when gift was added)
              if (sessionStorage.getItem('aura_trigger_toast') === 'true') {
                setTimeout(() => {
                  this.#showGiftToast();
                  sessionStorage.removeItem('aura_trigger_toast');
                }, 500);
              }
            }

          }
        });
      });
      this.#drawerObserver.observe(dialog, { attributes: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(ThemeEvents.cartAdd, this.#checkFreeGift);
    document.removeEventListener('click', this.#handleUpsellClick);
    if (this.#drawerObserver) this.#drawerObserver.disconnect();
  }

  // ==========================================
  // YOUR EXACT UPSELL LOGIC (Secured)
  // ==========================================
  #handleUpsellClick = async (event) => {
    const button = event.target.closest('.boutique-upsell-btn');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.disabled) return;

    const variantId = button.getAttribute('data-variant-id');
    const productId = button.getAttribute('data-product-id');

    if (!variantId) return;

    const originalText = button.textContent;

    button.disabled = true;
    button.textContent = 'Adding...';
    button.style.opacity = '0.7';

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Cart add failed');
      }

      await response.json();

      button.textContent = 'Added ✓';
      button.style.backgroundColor = '#48186e';
      button.style.color = '#ffffff';
      button.style.opacity = '1';

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          sourceId: variantId,
          data: {
            source: 'cart-upsell-grid',
            itemCount: 1,
            productId: productId,
            variantId: variantId
          }
        }
      }));

      setTimeout(() => {
        button.textContent = originalText;
        button.style.backgroundColor = '';
        button.style.color = '';
        button.disabled = false;
      }, 2000);

    } catch (error) {
      button.textContent = 'Error';
      button.style.opacity = '1';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  };

  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;
    if (quantity === 0) return this.onLineItemRemove(line);

    this.updateQuantity({ line, quantity, action: 'change' });
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
        this.#updateCartQuantitySelectorButtonStates();
        
        setTimeout(() => this.#checkMilestones(), 300);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  handleDiscountUpdate = (event) => {
    this.#checkFreeGift();
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = (event) => {
    this.#checkFreeGift();
    
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      setTimeout(() => this.#checkMilestones(), 400); 
      return;
    }
    
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
      setTimeout(() => this.#checkMilestones(), 300); 
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      setTimeout(() => this.#checkMilestones(), 400); 
    }
  };

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

 #checkFreeGift = async () => {
    if (this.#checkingGift) return;
    this.#checkingGift = true;

    const FREE_GIFT_VARIANT = 47141349621911;
    const CART_THRESHOLD = 249900;

    try {
      const cart = await fetch('/cart.js').then(r => r.json());

      let realSubtotal = 0;
      let giftItem = null;
      let duplicatesToRemove = [];

      cart.items.forEach(item => {
        // Identify the FREE gift strictly by its hidden property
        if (item.variant_id === FREE_GIFT_VARIANT && item.properties && item.properties._free_gift) {
          if (!giftItem) {
            giftItem = item; // First free gift found
          } else {
            duplicatesToRemove.push(item); // Any extra free gift line items
          }
        } else {
          // This is a PAID item. Even if it's the exact same Pearl Studs, it lacks the _free_gift property.
          realSubtotal += item.original_line_price;
        }
      });

      // 🧹 Clean up any duplicate free gifts if the cart glitches
      for (const duplicate of duplicatesToRemove) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: duplicate.key, quantity: 0 })
        });
      }

      // 🧹 Force the free gift to strictly have a quantity of 1
      if (giftItem && giftItem.quantity > 1) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: giftItem.key, quantity: 1 })
        });
      }

      // ADD FREE GIFT
      if (realSubtotal >= CART_THRESHOLD && !giftItem) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{
              id: FREE_GIFT_VARIANT,
              quantity: 1,
              properties: { _free_gift: true }
            }]
          })
        });

        sessionStorage.setItem('aura_trigger_toast', 'true');
        sectionRenderer.renderSection(this.sectionId, { cache: false });
        setTimeout(() => this.#checkMilestones(), 400);
      }

      // REMOVE FREE GIFT
      if (realSubtotal < CART_THRESHOLD && giftItem) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: giftItem.key, quantity: 0 })
        });

        sectionRenderer.renderSection(this.sectionId, { cache: false });
        setTimeout(() => this.#checkMilestones(), 400);
      }

    } catch (error) {
      console.error('Free gift logic error:', error);
    } finally {
      this.#checkingGift = false;
    }
  };
  // ==========================================
  // DELAYED QUEUE CONFETTI LOGIC & TOAST TRIGGER
  // ==========================================

 async #checkMilestones() {
    const dialog = this.closest('dialog');
    const isDrawerOpen = dialog && dialog.hasAttribute('open');
    
    // 🟢 SAFE TOAST TRIGGER: Only runs after HTML is fully updated and stable
    if (sessionStorage.getItem('aura_trigger_toast') === 'true') {
      if (isDrawerOpen) {
        this.#showGiftToast();
        sessionStorage.removeItem('aura_trigger_toast');
      }
    }

    try {
      // 1. Fetch the absolute latest cart data directly from Shopify
      const cart = await fetch('/cart.js').then(r => r.json());

      // 2. Calculate the true subtotal (ignoring gifts and post-discount drops)
      let realSubtotal = 0;
      cart.items.forEach(item => {
        if (!item.properties || !item.properties._free_gift) {
          realSubtotal += item.original_line_price;
        }
      });

      // 3. Map the subtotal directly to your 3 milestones
      let currentCount = 0;
      if (realSubtotal >= 1000000) {        // 10k milestone
        currentCount = 3;
      } else if (realSubtotal >= 249900) {  // 2.49k milestone
        currentCount = 2;
      } else if (realSubtotal >= 150000) {  // 1.5k milestone
        currentCount = 1;
      }

      // 4. Trigger Confetti if we leveled up!
      if (currentCount > this.#lastUnlockedCount) {
        const isMaxed = currentCount >= 3;    
        
        // If drawer is open, blast right now. If closed, queue it up.
        if (isDrawerOpen) {
          this.#triggerConfetti(isMaxed);
        } else {
          this.#pendingConfetti = { isMaxed };
        }
      }

      this.#lastUnlockedCount = currentCount;
      sessionStorage.setItem('aura_unlocked_memory', currentCount.toString());

    } catch (error) {
      console.error('Milestone calculation error:', error);
    }
  }

  #triggerConfetti(isMaxed) {
    const colors = ['#48186e', '#9D4EDD', '#D4AF37', '#C0C0C0'];

    const fire = () => {
      const canvas = document.createElement('canvas');
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '999999';
      
      if (window.getComputedStyle(this).position === 'static') {
        this.style.position = 'relative';
      }
      
      this.appendChild(canvas);

      const myConfetti = window.confetti.create(canvas, { resize: true, useWorker: true });
      const particleCount = isMaxed ? 120 : 60;

      const defaults = { 
        colors: colors, 
        gravity: 0.35, 
        spread: 60,
        ticks: 300,
        zIndex: 999999
      };

      myConfetti(Object.assign({}, defaults, { particleCount, origin: { x: 0, y: 1 }, angle: 60 }));
      myConfetti(Object.assign({}, defaults, { particleCount, origin: { x: 1, y: 1 }, angle: 120 }));

      setTimeout(() => canvas.remove(), 4000); 
    };

    if (typeof window.confetti === 'undefined') {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js';
      script.onload = fire;
      document.head.appendChild(script);
    } else {
      fire();
    }
  }

  async #showGiftToast() {
    const toast = document.getElementById('AuraGiftToast');
    if (!toast) return;

    // 1. Open the dialog so it sits on the absolute Top Layer
    if (!toast.open) {
      toast.showModal();
    }

    // 2. Trigger the CSS slide-in
    requestAnimationFrame(() => {
      toast.classList.add('toast-active');
    });

    // 3. Wait exactly 6 seconds securely
    await new Promise(resolve => setTimeout(resolve, 6000));

    // 4. Slide it out
    toast.classList.remove('toast-active');

    // 5. Wait for the slide out animation to finish, then fully close it
    await new Promise(resolve => setTimeout(resolve, 600));
    toast.close();
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}